import {
  useDeleteLegalClientLegalHead,
  useLegalClient,
  useLegalClientLegalHead as useLegalClientLegalHeadApi,
  useLegalClientLegalHeadArchive,
  useLegalClientLegalHeadDearchive,
} from 'api/requests';
import { useCountries } from 'api/requests/country';
import { PermissionType } from 'api/types/entity';
import { NavigationBreadcrumbsTitles } from 'components';
import { useServerErrorNotify } from 'hooks';
import { FormSubmit } from 'libs/form/Form';
import { useTranslation } from 'libs/i18n';
import { ROUTES, useNavigate, RoutesId } from 'libs/navigation';
import { useNotify } from 'libs/notify';
import { usePermissions } from 'libs/permissions';

import { LegalClientLegalHeadFormType } from '../forms/legalClientLegalHeadForm';
import { getLegalClientLegalHeadInitialValue } from '../helpers';

import { useLegalClientLegalHeadFirstHeadActions } from './useLegalClientLegalHeadFirstHeadActions';
import { useLegalHeadPageTitle } from './useLegalHeadPageTitle';
import { useLockLegalClientLegalHead } from './useLockLegalClientLegalHead';
import { useUpdateLegalClientLegalHead } from './useUpdateLegalClientLegalHead';

type NavigateParams = {
  legalClientId: string;
  headId: string;
};

export const useLegalClientLegalHead = () => {
  const { t } = useTranslation();

  const { params, navigate } = useNavigate<NavigateParams>();

  const notify = useNotify();

  const {
    loading,
    legalClientLegalHead,
    error,
    refetch: refetchLegalClientLegalHead,
  } = useLegalClientLegalHeadApi(params.headId!);

  const { legalClient } = useLegalClient(params.legalClientId!);

  const { loading: submitLoading, updateLegalClientLegalHead } =
    useUpdateLegalClientLegalHead(legalClient);

  const { countries, loading: countriesLoading } = useCountries();

  const { lockClientIfPossible, unlockClient } = useLockLegalClientLegalHead(
    legalClient,
    legalClientLegalHead
  );

  const {
    deleteLegalClientLegalHead,
    loading: deleteLoading,
    error: deleteError,
  } = useDeleteLegalClientLegalHead();

  const { checkPermissions } = usePermissions();

  const {
    loading: archiveLoading,
    archive,
    error: archiveError,
  } = useLegalClientLegalHeadArchive();

  const {
    loading: dearchiveLoading,
    dearchive,
    error: dearchiveError,
  } = useLegalClientLegalHeadDearchive();

  const initialValues = legalClientLegalHead
    ? getLegalClientLegalHeadInitialValue(legalClientLegalHead, t)
    : undefined;

  const breadcrumbsTitles: NavigationBreadcrumbsTitles = {
    [RoutesId.legalClientLegalHead]: legalClientLegalHead
      ? t('client.legal.legalClientLegalHead.title', {
          name: legalClientLegalHead.fullName,
        })
      : undefined,
    [RoutesId.legalClient]: legalClient?.fullName,
  };

  const onSubmit: FormSubmit<LegalClientLegalHeadFormType> = async (
    values: LegalClientLegalHeadFormType,
    helpers
  ) => {
    if (await updateLegalClientLegalHead(legalClientLegalHead!, values)) {
      helpers.setReadOnly(true);
    }
  };

  const onShowVersionHistory =
    legalClient &&
    legalClientLegalHead?.versionNumber &&
    legalClientLegalHead?.versionNumber > 1
      ? () => {
          navigate(
            ROUTES.clients.legalClients.legalClient.legalClientVersion
              .legalClientLegalHeadVersion.fullPath,
            {
              legalClientId: legalClient.id,
              headId: params.headId!,
              versionId: legalClientLegalHead.versionId!,
            }
          );
        }
      : undefined;

  const onDelete = checkPermissions([PermissionType.LegalClientHead_DeleteOne])
    ? async (reason: string) => {
        if (
          await deleteLegalClientLegalHead({
            id: params.headId!,
            reason,
            versionNumber: legalClient?.versionNumber!,
          })
        ) {
          notify.info(t('client.legal.legalClientHead.successDeleteMessage'));
          navigate(
            ROUTES.clients.legalClients.legalClient.fullPath,
            {
              legalClientId: params.legalClientId!,
            },
            { replace: true }
          );
        }
      }
    : undefined;

  const onArchive =
    checkPermissions([PermissionType.LegalClientChildEntity_ArchiveOne]) &&
    !legalClientLegalHead?.archived
      ? async (reason: string) => {
          if (
            await archive(params.headId!, {
              reason,
              versionNumber: legalClientLegalHead?.versionNumber!,
            })
          ) {
            notify.info(t('common.archivedMessage'));
            return true;
          }
          return false;
        }
      : undefined;

  const onDearchive =
    checkPermissions([PermissionType.LegalClientChildEntity_ArchiveOne]) &&
    legalClientLegalHead?.archived
      ? async (reason: string) => {
          const updatedLegalHead = await dearchive(params.headId!, {
            reason,
            versionNumber: legalClientLegalHead?.versionNumber!,
          });

          if (updatedLegalHead) {
            notify.info(t('common.dearchivedMessage'));
            if (
              !updatedLegalHead.legalClientBodies?.length &&
              updatedLegalHead.hasLegalClientBody
            ) {
              notify.show(
                'error',
                t('client.legal.legalClientHead.bodyErrorMessage')
              );
            }

            return true;
          }
          return false;
        }
      : undefined;

  const onClickFirstHead = (firstHeadId: string) => {
    navigate(
      ROUTES.clients.legalClients.legalClient.legalClientLegalHead
        .legalClientFirstHead.fullPath,
      {
        legalClientId: params.legalClientId!,
        headId: params.headId!,
        firstHeadId,
      }
    );
  };

  useLegalHeadPageTitle(legalClient, legalClientLegalHead);

  const {
    dialogAddFirstHeadDialogProps,
    onArchiveFirstHead,
    onClickAddFirstHead,
    onDearchiveFirstHead,
    onDeleteFirstHead,
  } = useLegalClientLegalHeadFirstHeadActions(
    legalClient,
    legalClientLegalHead,
    refetchLegalClientLegalHead
  );

  useServerErrorNotify(deleteError ?? archiveError ?? dearchiveError);

  return {
    legalClientLegalHead,
    legalClient,
    loading: !legalClientLegalHead && loading,
    countries: countries ?? [],
    countriesLoading,
    error,
    submitLoading,
    initialValues,
    breadcrumbsTitles,
    deleteLoading,
    archiveLoading,
    dearchiveLoading,
    onDeleteFirstHead,
    onDearchiveFirstHead,
    lockClientIfPossible,
    unlockClient,
    onSubmit,
    onShowVersionHistory,
    refetchLegalClientLegalHead,
    onDelete,
    onArchive,
    onDearchive,
    onArchiveFirstHead,
    onClickAddFirstHead,
    dialogAddFirstHeadDialogProps,
    onClickFirstHead,
  };
};
